import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import ModernHeader from "./Sidebar/ModernHeaderNew";
import ModernSidebar from "./Sidebar/ModernSidebar";
import "./ModernComponents.css";

/**
 * ModernLayout - Main layout wrapper for the application
 * 
 * Features:
 * - Fixed header at the top
 * - Collapsible sidebar (80px collapsed / 256px expanded)
 * - Main content area with responsive padding
 * - Smooth transitions between collapsed/expanded states
 * - Mobile responsive: sidebar hidden by default, shown via hamburger toggle
 * 
 * @component
 */
const ModernLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  // Détecter les changements de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);

      // Fermer le menu mobile si on passe en mode desktop
      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Bloquer le scroll du body quand le menu mobile est ouvert
  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobile, isMobileMenuOpen]);

  const toggleSidebar = () => {
    if (isMobile) {
      // En mobile : toggle le menu mobile
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else {
      // En desktop : toggle collapsed/expanded
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="modern-layout">
      {/* Overlay pour fermer le menu mobile */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="modern-layout__overlay"
          onClick={closeMobileMenu}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
              closeMobileMenu();
            }
          }}
          aria-label="Fermer le menu"
        />
      )}

      {/* Sidebar */}
      <ModernSidebar
        isCollapsed={isSidebarCollapsed}
        isMobile={isMobile}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      {/* Main content area */}
      <div className={`modern-layout__main-wrapper ${isMobile ? 'modern-layout__main-wrapper--mobile' : (isSidebarCollapsed ? 'modern-layout__main-wrapper--collapsed' : 'modern-layout__main-wrapper--expanded')}`}>
        {/* Header */}
        <ModernHeader onToggleSidebar={toggleSidebar} isCollapsed={isSidebarCollapsed} />

        {/* Combined Body Area */}
        <div className="modern-layout__body">
          <main className="main-wrapper modern-layout__content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default ModernLayout;
